import api from '../helpers';

const mapper = (model) => ({
  // eslint-disable-next-line no-underscore-dangle
  id: model.id || model._id,
  ...model,
});

export default {
  getLeak: {
    key: 'fetchLeak',
    query: (id, field) => {
      return api.get(`/api/leaks/${id}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        params: { by: field },
      });
    },
    mapper: ({ data }) => mapper(data),
  },
  // PRO-23: preview the US-DoE gasCostSummary a running-state snapshot would apply to
  // this leak (single-record gas-cost picker), without saving.
  getRunningStateCost: {
    key: 'fetchLeakRunningStateCost',
    query: (id, field, runningStateLogId) => {
      return api.get(`/api/leaks/${id}/running-state-cost`, {
        headers: { 'Content-Type': 'application/json' },
        params: { by: field, runningStateLogId },
      });
    },
    mapper: ({ data }) => data,
  },
  setLeak: {
    query: (id, field, model) => {
      return api.put(`/api/leaks/${id}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        data: model,
        params: { by: field },
      });
    },
  },
  list: {
    key: 'fetchLeaks',
    query: (offset, limit, sortBy, sortDir, filters = {}, companyId) => {
      return api.get('/api/leaks', {
        headers: {
          'Content-Type': 'application/json',
        },
        params: {
          format: 'withRelations',
          offset,
          limit,
          sort: [sortBy, sortDir],
          filters,
          companyId,
        },
      });
    },
    mapper: ({ data }) => data?.map(mapper) || [],
  },
  listIds: {
    key: 'fetchLeakIds',
    query: (filters) => {
      return api.get('/api/leaks', {
        headers: {
          'Content-Type': 'application/json',
        },
        params: {
          format: 'onlyId',
          offset: 0,
          limit: -1,
          filters,
        },
      });
    },
    mapper: ({ data }) => data,
  },
  transfer: {
    key: 'transferLeaks',
    query: (oldUserId, newUserId) => {
      return api.post('/api/leaks/transfer', {
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          fromUserId: oldUserId,
          toUserId: newUserId,
        },
      });
    },
  },
  move: {
    key: 'moveLeaks',
    query: (leakIds, toUserId) => {
      return api.post('/api/leaks/move', {
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          leakIds,
          toUserId,
        },
      });
    },
  },
  bulkCompanyEdit: {
    key: 'bulkCompanyEdit',
    query: (leakIds, targetCompanyId) => {
      return api.post('/api/leaks/bulk-company-edit', {
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          leakIds,
          targetCompanyId,
        },
      });
    },
  },
  bulkDeleteById: {
    key: 'bulkDelete',
    query: (ids) => {
      return api.post('/api/leaks/bulk-delete', {
        headers: {
          'Content-Type': 'application/json',
        },
        data: { ids },
        params: { by: 'id' },
      });
    },
  },
  bulkDeleteByFilters: {
    key: 'bulkDelete',
    query: (filters) => {
      return api.post('/api/leaks/bulk-delete', {
        headers: {
          'Content-Type': 'application/json',
        },
        data: { filters },
      });
    },
  },
  bulkEditById: {
    key: 'bulkEdit',
    query: (ids, model) => {
      return api.post('/api/leaks/bulk-edit', {
        headers: {
          'Content-Type': 'application/json',
        },
        data: { ids, data: model },
        params: { by: 'id' },
      });
    },
  },
  bulkEditByDocumentId: {
    key: 'bulkEdit',
    query: (documentId, model) => {
      return api.post('/api/leaks/bulk-edit', {
        headers: {
          'Content-Type': 'application/json',
        },
        data: { documentId, data: model },
      });
    },
  },
  // Dedicated bulk-edit route for system parameters (gas cost), backed by the
  // bulk_edit_record_system_parameters permission so user-role accounts can run it.
  bulkSystemParametersEditById: {
    key: 'bulkSystemParametersEdit',
    // model is optional: omit → backend auto-selects the latest snapshot; provide → it applies
    // the chosen log. expected = the raw company config previewed; a mismatch is refused with B03:022.
    query: (ids, model, expected) => {
      return api.post('/api/leaks/bulk-system-parameters', {
        headers: {
          'Content-Type': 'application/json',
        },
        data: model ? { ids, data: model, expected } : { ids, expected },
        params: { by: 'id' },
      });
    },
  },
  // Single-record system-parameters apply: the backend derives method, latest snapshot, per-gas
  // price and energyCost; expected = the raw company config previewed, refused with B03:022 if it drifted.
  updateSystemParametersById: {
    key: 'updateLeakSystemParameters',
    query: (documentId, model, expected) => {
      return api.post(`/api/leaks/${documentId}/system-parameters`, {
        headers: {
          'Content-Type': 'application/json',
        },
        data: model ? { data: model, expected } : { expected },
      });
    },
    mapper: ({ data }) => mapper(data),
  },
  calculate: {
    key: 'calculateLeak',
    query: (parameters) => {
      return api.post('/api/calculations', {
        headers: {
          'Content-Type': 'application/json',
        },
        data: parameters,
      });
    },
  },
  history: {
    key: 'historyLeak',
    query: (resourceId) => {
      return api.get(`/api/history/leaks/${resourceId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
    },
  },
  downloadRecordImages: {
    key: 'downloadRecordImages',
    query: (documentId, localizationHeaders = {}) => {
      return api.get(`/api/leaks/${documentId}/images/download`, {
        headers: { ...localizationHeaders },
        params: { by: 'documentId' },
        responseType: 'blob',
      });
    },
  },
  exportCsv: {
    key: 'exportLeaksCsv',
    query: (filters = {}, exportOptions = {}, localizationHeaders = {}) => {
      return api.post('/api/leaks/export/csv', {
        headers: {
          'Content-Type': 'application/json',
          ...localizationHeaders,
        },
        params: {
          format: 'withRelations',
          offset: 0,
          limit: -1,
          sort: ['createdAt', 'desc'],
          filters,
        },
        data: exportOptions,
        responseType: 'blob',
      });
    },
  },
};
